import { observer } from "mobx-react"
import * as React from "react"
import "react-day-picker/lib/style.css"
import MonthsDropdown from "../../utils/MonthsDropdown"
import DaysDropdown from "../../utils/DaysDropdown"
import YearInput from "../../utils/YearInput"
import { MemberForm } from "./MemberForm"
import { Flex } from "rebass"
import Input from "../../components/Input"
import theme from "../../theme"
import InputWrapper from "../../components/InputWrapper"
import InputLabel from "../../components/InputLabel"
import InputIconWrapperEnd from "../../components/InputIconWrapperEnd"
import InputIconWrapperBegin from "../../components/InputIconWrapperBegin"
import { isValidEmail, isValidPhone } from "../../../light/utils"
import SelectWrapper from "BookingSystem/components/SelectWrapper"
import Select from "BookingSystem/components/Select"
import { Member } from "BookingSystem/db/Member"

const BasicMemberForm = observer(({ member }: { member: Member }) => {
  return (
    <React.Fragment>
      <fieldset style={{ border: "none" }}>
        <label>Name & DOB</label>
        <div className="field">
          <InputWrapper m={["5%"]} mb={["7%"]}>
            <InputLabel modal>First Name</InputLabel>
            <Input
              modal
              autoComplete="given-name"
              type="text"
              placeholder="First Name"
              value={member.object.firstName}
              onChange={(event: any) => {
                member.object.firstName = event.target.value
              }}
            />
            {member.isValidFirstName != null && !member.isValidFirstName ? (
              <InputIconWrapperEnd>
                <i
                  className="fas fa-exclamation-triangle"
                  style={{ color: theme.colors.danger }}
                />
              </InputIconWrapperEnd>
            ) : (
              <InputIconWrapperEnd />
            )}
          </InputWrapper>
        </div>
        <div className="field">
          <InputWrapper m={["5%"]} mb={["7%"]}>
            <InputLabel modal>Last Name</InputLabel>
            <Input
              modal
              value={member && member.object.lastName}
              autoComplete="family-name"
              onChange={(event: any) => {
                member.object.lastName = event.target.value
              }}
              type="text"
              placeholder="Last Name"
            />{" "}
            {member.isValidLastName != null && !member.isValidLastName ? (
              <InputIconWrapperEnd>
                <i
                  className="fas fa-exclamation-triangle"
                  style={{ color: theme.colors.danger }}
                />
              </InputIconWrapperEnd>
            ) : (
              <InputIconWrapperEnd />
            )}
          </InputWrapper>
        </div>
        <div className="field">
          <SelectWrapper m={["5%"]} mb={["7%"]}>
            <InputLabel modal>Date of Birth</InputLabel>
            <Flex
              justifyContent="flex-end"
              alignItems="center"
              style={{
                width: "100%"
              }}
            >
              <MonthsDropdown
                member={member}
                style={{ height: 45, flex: 1, borderRadius: 0 }}
              />
              <DaysDropdown
                member={member}
                style={{
                  height: 45,
                  flex: 1,
                  borderRadius: 0,
                  marginLeft: "1%"
                }}
              />
              <YearInput
                member={member}
                style={{
                  height: 45,
                  flex: 1,
                  borderRadius: 0,
                  marginLeft: "1%"
                }}
              />
            </Flex>
          </SelectWrapper>
        </div>
      </fieldset>
    </React.Fragment>
  )
})

const EmailPhoneForm = observer(({ member }: { member: Member }) => {
  return (
    <fieldset style={{ border: "none" }}>
      <label>Contact</label>
      <div className="field">
        <InputWrapper m={["5%"]} mb={["7%"]}>
          <InputIconWrapperBegin>
            <i className="fas fa-envelope" />
          </InputIconWrapperBegin>
          <InputLabel modal>Email</InputLabel>
          <Input
            hasIcon
            modal
            autoComplete="email"
            type="email"
            placeholder="email@domain.com"
            value={member.object.email}
            onChange={(event: any) => {
              member.object.email = event.target.value
            }}
          />
          {!isValidEmail(member.object.email) ? (
            <InputIconWrapperEnd>
              <i
                className="fas fa-exclamation-triangle"
                style={{ color: theme.colors.danger }}
              />
            </InputIconWrapperEnd>
          ) : (
            <InputIconWrapperEnd />
          )}
        </InputWrapper>
      </div>
      <div className="field">
        <InputWrapper m={["5%"]} mb={["7%"]}>
          <InputLabel modal>Phone</InputLabel>
          <InputIconWrapperBegin>
            <i className="fas fa-phone" />
          </InputIconWrapperBegin>
          <Input
            hasIcon
            modal
            autoComplete="on"
            type="tel"
            placeholder="303-303-3033"
            value={member.object.phone}
            onChange={(event: any) => {
              member.object.phone = event.target.value
            }}
          />
          {!isValidPhone(member.object.phone) ? (
            <InputIconWrapperEnd>
              <i
                className="fas fa-exclamation-triangle"
                style={{ color: theme.colors.danger }}
              />
            </InputIconWrapperEnd>
          ) : (
            <InputIconWrapperEnd />
          )}
        </InputWrapper>
      </div>
    </fieldset>
  )
})

export const MemberFormComp = observer(function MemberForm({
  model
}: {
  model: MemberForm
}) {
  const children = []
  const { member, wasFound } = model
  children.push(<BasicMemberForm key={0} member={model.member} />)

  if (wasFound)
    children.push(
      <div
        key={1}
        className="notification is-success"
        style={{
          marginTop: "2%",
          marginBottom: "2%"
        }}
      >
        Member found. Welcome back!
      </div>
    )
  else if (member && member.isValidBasic && member.isAdult) {
    children.push(<EmailPhoneForm key={3} member={member} />)
    children.push(<AddressForm key={4} member={member} />)
  }
  return (
    <form
      style={{
        width: "100%",
        height: "100%",
        background: theme.colors.modalBody
      }}
    >
      {children}
    </form>
  )
})

export const AddressForm = observer(({ member }: { member: Member }) => {
  let states = [
    "State",
    "AK",
    "AL",
    "AR",
    "AS",
    "AZ",
    "CA",
    "CO",
    "CT",
    "DC",
    "DE",
    "FL",
    "GA",
    "GU",
    "HI",
    "IA",
    "ID",
    "IL",
    "IN",
    "KS",
    "KY",
    "LA",
    "MA",
    "MD",
    "ME",
    "MI",
    "MN",
    "MO",
    "MS",
    "MT",
    "NC",
    "ND",
    "NE",
    "NH",
    "NJ",
    "NM",
    "NV",
    "NY",
    "OH",
    "OK",
    "OR",
    "PA",
    "PR",
    "RI",
    "SC",
    "SD",
    "TN",
    "TX",
    "UT",
    "VA",
    "VI",
    "VT",
    "WA",
    "WI",
    "WV",
    "WY"
  ]
  return (
    <fieldset
      style={{
        border: "none"
      }}
    >
      <label>Address</label>
      <div className="field">
        <InputWrapper m={["5%"]} mb={["7%"]}>
          <InputLabel modal>Street</InputLabel>
          <Input
            modal
            type="text"
            autoComplete="address"
            value={member.object.street}
            onChange={(event: any) => {
              member.object.street = event.target.value
            }}
            placeholder="Street"
          />
          {member.isValidStreet != null && !member.isValidStreet ? (
            <InputIconWrapperEnd>
              <i
                className="fas fa-exclamation-triangle"
                style={{ color: theme.colors.danger }}
              />
            </InputIconWrapperEnd>
          ) : (
            <InputIconWrapperEnd />
          )}
        </InputWrapper>
      </div>
      <div className="field">
        <InputWrapper m={["5%"]} mb={["7%"]}>
          <InputLabel modal>City</InputLabel>
          <Input
            modal
            type="text"
            autoComplete="on"
            value={member.object.city}
            onChange={(event: any) => {
              member.object.city = event.target.value
            }}
            placeholder="City"
          />
          {member.isValidCity != null && !member.isValidCity ? (
            <InputIconWrapperEnd>
              <i
                className="fas fa-exclamation-triangle"
                style={{ color: theme.colors.danger }}
              />
            </InputIconWrapperEnd>
          ) : (
            <InputIconWrapperEnd />
          )}
        </InputWrapper>
      </div>
      <div className="field">
        <SelectWrapper m={["5%"]} mb={["7%"]}>
          <InputLabel modal>State</InputLabel>
          <Select
            value={member.object.state}
            style={{
              width: "100%",
              height: 45,
              fontFamily: theme.fonts.tertiary,
              fontSize: "1.5rem"
            }}
            onChange={(event: any) => {
              member.object.state = event.target.value
            }}
          >
            {states.map((state: string, i: number) => (
              <option key={i} value={state}>
                {state}
              </option>
            ))}
          </Select>
        </SelectWrapper>
      </div>
      <div className="field">
        <InputWrapper m={["5%"]} mb={["7%"]}>
          <InputLabel modal>Zip</InputLabel>
          <Input
            modal
            type="text"
            autoComplete="on"
            placeholder="Zip"
            value={member.object.zip}
            onChange={(event: any) => {
              member.object.zip = event.target.value
            }}
          />
          {member.isValidZip != null && !member.isValidZip ? (
            <InputIconWrapperEnd>
              <i
                className="fas fa-exclamation-triangle"
                style={{ color: theme.colors.danger }}
              />
            </InputIconWrapperEnd>
          ) : (
            <InputIconWrapperEnd />
          )}
        </InputWrapper>
      </div>
    </fieldset>
  )
})
